<script setup lang="ts">
import {
  DialogSize,
  KButton,
  KConfirmDialog,
} from "@kaleidoscope-solutions/ks-design-system"
import { useClipboard } from "@vueuse/core"
import { computed, ref } from "vue"
import { closeDialog } from "vue3-promise-dialog"
import CopyIcon from "@/components/FormulaPlayground/CopyIcon.svg?component"

const props = withDefaults(defineProps<DialogProps>(), {
  size: DialogSize.small,
  okButton: "OK",
  cancelButton: "Cancel",
  copyButton: "Copy",
})

export interface DialogProps {
  title: string
  body: string | string[]
  size?: DialogSize
  okButton?: string
  okButtonRed?: boolean
  cancelButton?: string
  hideCancelButton?: boolean
  copyText?: string
  copyButton?: string
  preformatted?: boolean
}

const body = Array.isArray(props.body) ? props.body : [props.body]
const { copy, copied } = useClipboard()
const copyFailed = ref(false)
const copyButtonLabel = computed(() => {
  if (copied.value) {
    return "Copied"
  }

  if (copyFailed.value) {
    return "Copy failed"
  }

  return props.copyButton
})

async function copyToClipboard() {
  if (!props.copyText) {
    return
  }

  try {
    await copy(props.copyText)
    copyFailed.value = false
  } catch (error) {
    console.error("Failed to copy dialog text to clipboard:", error)
    copyFailed.value = true
  }
}
</script>

<template>
  <KConfirmDialog
    :open="true"
    :size="size"
  >
    <template #default>
      <h2 class="mb-4 text-xl font-bold">
        {{ title }}
      </h2>
      <div
        class="mb-6 max-h-[60vh] overflow-auto"
      >
        <template
          v-for="text in body"
          :key="text"
        >
          <pre
            v-if="preformatted"
            class="rounded-sm border border-gray-300 bg-gray-50 p-3 font-mono text-sm whitespace-pre-wrap text-gray-900"
          >{{ text }}</pre>
          <p v-else>
            {{ text }}
          </p>
        </template>
      </div>
      <div class="flex w-full justify-end gap-4">
        <KButton
          v-if="copyText"
          variety="secondary"
          color="violet"
          data-test="promise-dialog-btn-copy"
          @click="copyToClipboard"
        >
          <template #left>
            <CopyIcon class="size-4" />
          </template>
          <template #default>
            {{ copyButtonLabel }}
          </template>
        </KButton>
        <KButton
          v-if="!hideCancelButton"
          variety="secondary"
          color="violet"
          data-test="promise-dialog-btn-cancel"
          @click="closeDialog(false)"
        >
          {{ cancelButton }}
        </KButton>
        <KButton
          :color="okButtonRed ? 'red' : 'violet'"
          data-test="promise-dialog-btn-ok"
          @click="closeDialog(true)"
        >
          {{ okButton }}
        </KButton>
      </div>
    </template>
  </KConfirmDialog>
</template>
